import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useForm, useWatch } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { RenderStep } from "../../components/ApplicationForm/RenderStep";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import { NavbarApplicationInfo } from "../../components/CommonComponents/Navbar/NavbarApplicationInfo";
import { SuccessToast } from "../../components/toastMessage";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { canRoleEditApplications } from "../../config/reviewerApplicationEdit";
import { useGetFieldLabel } from "../../contexts/LanguageContext";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { useUser } from "../../contexts/UserContext";
import { TPopulatedForm } from "../../hooks/applications.hooks";
import { RoadWidthNoteContext } from "../../components/RoadWidthNote/RoadWidthNote";
import {
  TReviewerEditState,
  useReviewerEditState,
  useSaveReviewerEdit,
} from "../../hooks/reviewer-application-edit.hooks";
import {
  ReviewerEditFieldChange,
  getReviewerEditChanges,
} from "../../utils/reviewerEditChanges";

type EditableField = NonNullable<TPopulatedForm["fields"]>[number];

/**
 * Edit Application page for reviewers. One page for Level 1, 2 and 3: it
 * shows the applicant's form fields pre-filled, drawn by the same RenderStep
 * the applicant's own form uses, and saves only what the reviewer changed.
 *
 * Opened from the Edit button on a reviewer's application details screen
 * with `{ id, returnTo }` in the navigation state.
 */
export function ReviewerEditApplication() {
  const location = useLocation();
  const user = useUser();
  const id = Number(location.state?.id);
  const returnTo: string | undefined = location.state?.returnTo;

  const stateQuery = useReviewerEditState({ id });
  const state = stateQuery.data;

  const setStuff = useSetStuffToRenderOnNavbar();
  useEffect(() => {
    setStuff(
      <NavbarApplicationInfo
        applicationId={state?.application?.applicationId}
        status={state?.application?.status}
        applicationType={state?.application?.rl_application_type}
        currentPhase={state?.application?.current_phase}
      />
    );
    return () => setStuff(null);
  }, [setStuff, state?.application]);

  if (!id || !canRoleEditApplications(user?.role)) {
    return <Navigate to="/" replace />;
  }

  if (stateQuery.isFetching || stateQuery.isError || !state) {
    return (
      <div className="flex justify-center items-center p-8">
        {stateQuery.isFetching ? (
          <SpinnerCircular />
        ) : (
          <p>Something went wrong while fetching info!</p>
        )}
      </div>
    );
  }

  return (
    <div className="px-2 pb-2 md:p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-300 p-8">
        {/* The Application ID, type and status are shown in the navbar
            (NavbarApplicationInfo above), as on the other reviewer screens,
            so they are not repeated under the title. */}
        <p className="text-center mb-8 text-grey-600 text-2xl font-bold">
          Edit Application
        </p>

        {!state.canEdit && (
          <p className="text-center mb-6 font-semibold text-red-600">
            {state.lockReason}
          </p>
        )}

        {/*
          "edit": an old road width stays listed and selected, with a note
          telling the reviewer to choose a width or return the field.
        */}
        <RoadWidthNoteContext.Provider value="edit">
          <RenderStep
            key={`reviewer-edit-${state.application.id}`}
            step={state.form}
            defaultValues={state.application}
            onChangeService={() => null}
          >
            {(formMethods) => (
              <ReviewerEditFooter
                formMethods={formMethods}
                state={state}
                returnTo={returnTo}
              />
            )}
          </RenderStep>
        </RoadWidthNoteContext.Provider>
      </div>
    </div>
  );
}

/**
 * Everything below the fields: the list of changes, the Remark box, the
 * buttons and the confirmation box. Rendered inside RenderStep, so it reads
 * the same form the fields write to.
 */
function ReviewerEditFooter({
  formMethods,
  state,
  returnTo,
}: {
  formMethods: ReturnType<typeof useForm>;
  state: TReviewerEditState;
  returnTo?: string;
}) {
  const navigate = useNavigate();
  const convert = useGetFieldLabel();
  const saveMutation = useSaveReviewerEdit();
  const [remark, setRemark] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { application, form, canEdit } = state;
  /**
   * Only the fields the reviewer can change. The read-only ones (Application
   * Type, Name of Service, the submitter fields) are shown on the form but
   * left out here, so they never appear in the list of changes and are
   * never sent with the save.
   * BEFORE: useMemo(() => form.fields || [], [form.fields]);
   */
  const fields: EditableField[] = useMemo(
    () => (form.fields || []).filter((field) => !field.readOnly),
    [form.fields]
  );

  const values = useWatch({ control: formMethods.control }) as Record<string, unknown>;

  const changes: ReviewerEditFieldChange<EditableField>[] = useMemo(
    () => getReviewerEditChanges(fields, application, values || {}),
    [fields, application, values]
  );

  const hasChanges = changes.length > 0;
  const cleanRemark = remark.trim();
  const isSaving = saveMutation.isPending;

  const goBack = () => {
    if (returnTo) navigate(returnTo, { state: { id: application.id } });
    else navigate(-1);
  };

  const onBack = () => {
    if (hasChanges && !window.confirm("Discard the changes you made?")) return;
    goBack();
  };

  const onReset = () => {
    formMethods.reset({ ...application });
    setRemark("");
  };

  const onReviewChanges = async () => {
    const isValid = await formMethods.trigger(undefined, { shouldFocus: true });
    if (isValid) setConfirmOpen(true);
  };

  const onConfirmSave = async () => {
    /**
     * Every editable field is sent with its current value, including the
     * ones left empty, so the backend can tell a cleared field from an
     * untouched one. It compares them with the stored values itself and
     * writes only what really changed.
     */
    const current = formMethods.getValues();
    const data = Object.fromEntries(
      fields.map((field) => [field.uid, current[field.uid] ?? null])
    );

    try {
      const result = await saveMutation.mutateAsync({
        id: application.id,
        data,
        remark: cleanRemark,
      });
      if (!result.data?.success) return;
      SuccessToast("Application updated");
      goBack();
    } catch (err) {
      /** The reason is already shown by the shared axios error toast. */
      setConfirmOpen(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto mt-8 flex flex-col gap-6">
      <div>
        <p className="font-semibold text-lg text-grey-600 mb-2">Your changes</p>
        {hasChanges ? (
          <ChangesTable changes={changes} convert={convert} />
        ) : (
          <p className="text-base text-grey-600">No field has been changed yet.</p>
        )}
      </div>

      <div>
        <label
          htmlFor="reviewer-edit-remark"
          className="font-semibold text-lg text-grey-600 mb-2 block"
        >
          Remark <span className="text-red-600">*</span>
        </label>
        <textarea
          id="reviewer-edit-remark"
          rows={3}
          value={remark}
          disabled={!canEdit || isSaving}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Why is this application being corrected?"
          className="w-full border border-gray-400 rounded-lg p-3 text-base resize-none"
        />
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 w-full max-w-[650px] mx-auto">
        <button
          type="button"
          disabled={isSaving}
          onClick={onBack}
          className="w-full sm:w-auto bg-black px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base"
        >
          Back
        </button>
        <button
          type="button"
          disabled={isSaving || !hasChanges}
          onClick={onReset}
          className="w-full sm:w-auto bg-primary px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Reset
        </button>
        <button
          type="button"
          disabled={!canEdit || isSaving || !hasChanges || !cleanRemark}
          onClick={onReviewChanges}
          title={
            !canEdit
              ? state.lockReason || ""
              : !hasChanges
                ? "Change at least one field"
                : !cleanRemark
                  ? "Enter a remark"
                  : ""
          }
          className="w-full sm:w-auto bg-green-600 px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Save Changes
        </button>
      </div>

      <Dialog
        open={confirmOpen}
        onClose={() => !isSaving && setConfirmOpen(false)}
        className="p-6 md:min-w-[600px]"
      >
        <p className="font-bold text-xl text-grey-600 mb-4">
          {`Confirm changes to ${application.applicationId}`}
        </p>
        <ChangesTable changes={changes} convert={convert} />
        <p className="text-base text-grey-600 mt-4 break-words">
          <span className="font-semibold">Remark: </span>
          {cleanRemark}
        </p>
        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            disabled={isSaving}
            onClick={() => setConfirmOpen(false)}
            className="bg-black px-6 py-2 rounded-full font-bold text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={onConfirmSave}
            className="bg-green-600 px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300"
          >
            {isSaving ? "Saving" : "Confirm & Save"}
          </button>
        </div>
      </Dialog>
    </div>
  );
}

/**
 * Lists the changed fields. Drawn with the same DataTable and
 * tableCustomStyles as the Document Table on the reviewers' screens, so it
 * looks like the other tables in the application.
 */
function ChangesTable({
  changes,
  convert,
}: {
  changes: ReviewerEditFieldChange<EditableField>[];
  convert: ReturnType<typeof useGetFieldLabel>;
}) {
  const columns = useMemo(
    (): TableColumn<ReviewerEditFieldChange<EditableField>>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "Field Name",
        center: true,
        grow: 2,
        cell: (d) => <span className="text-center">{convert(d.field)}</span>,
      },
      {
        name: "Old Value",
        center: true,
        grow: 2,
        cell: (d) => (
          <span className="text-center break-words">{d.oldValue || "-"}</span>
        ),
      },
      {
        name: "New Value",
        center: true,
        grow: 2,
        cell: (d) => (
          <span className="text-center break-words font-semibold">
            {d.newValue || "-"}
          </span>
        ),
      },
    ],
    [convert]
  );

  return (
    <DataTable
      keyField="uid"
      className="mt-4 border border-none rounded-none"
      data={changes.map((change) => ({ ...change, uid: change.field.uid }))}
      customStyles={tableCustomStyles}
      columns={columns}
    />
  );
}
